import { useCallback, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { notificationApiRef } from '../../../../../apis/notificationApi';
import { toolsProvisioningApiRef } from '../../../../../apis/toolsProvisioningApi';
import { useUserProfile } from '../../../../utils/useGetUserProfile';
import { toActivityLogPhase } from '../dto/responseDto/runResults.response.dto';
import type { IActivityLogPhaseResponse } from '../interfaces/api/responses/runResults.response';
import type { IIqeRunResultsRequest } from '../interfaces/api/requests/runResults.request';
import type { IActivityLogPhase } from '../interfaces/ui';

export const useActivityLog = () => {
  const notificationApi = useApi(notificationApiRef);
  const toolsProvisioningApi = useApi(toolsProvisioningApiRef);
  const { accessToken } = useUserProfile();
  const [data, setData] = useState<IActivityLogPhase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamControllerRef = useRef<AbortController | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const reset = useCallback(() => {
    streamControllerRef.current?.abort();
    streamControllerRef.current = null;
    setData([]);
    setLoading(false);
    setError(null);
  }, []);

  const streamActivityLog = useCallback(
    async (
      request: IIqeRunResultsRequest,
      onComplete: () => void,
    ): Promise<void> => {
      streamControllerRef.current?.abort();
      const streamController = new AbortController();
      streamControllerRef.current = streamController;
      setError(null);
      setLoading(true);

      try {
        const response = await toolsProvisioningApi.getIqeActivityLogStream(
          request,
          accessToken,
          streamController.signal,
        );

        if (!response.body)
          throw new Error('Activity log stream is unavailable');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let hasReceivedFirstPhase = false;

        const appendLine = (line: string) => {
          const payload = line.trim();
          if (!payload) return;

          // SSE (Server-Sent Events) format: "data: {json}"
          if (!payload.startsWith('data:')) return;

          const jsonString = payload.slice(5).trim();
          if (!jsonString) return;

          try {
            const responseLine = JSON.parse(jsonString) as IActivityLogPhaseResponse;

            if (
              !responseLine ||
              typeof responseLine !== 'object' ||
              Array.isArray(responseLine) ||
              Object.keys(responseLine).length === 0
            ) {
              return;
            }

            const activityPhase = toActivityLogPhase(responseLine);

            if (!hasReceivedFirstPhase) {
              hasReceivedFirstPhase = true;
              setData([activityPhase]);
              setLoading(false);
              return;
            }

            setData(current => [...current, activityPhase]);
          } catch (error) {
            console.error('Failed to parse activity log line:', error);
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          lines.forEach(appendLine);
        }

        if (buffer.trim()) appendLine(buffer);
        onComplete();
      } catch (streamError) {
        if (!streamController.signal.aborted) {
          const message = `Error occurred - ${streamError}`;
          setError(message);
          notificationApi.sendNotification({
            message,
            disapperAfterMs: 4000,
            severity: 'error',
          });
        }
      } finally {
        if (streamControllerRef.current === streamController) {
          streamControllerRef.current = null;
          setLoading(false);
        }
      }
    },
    [accessToken, notificationApi, toolsProvisioningApi],
  );

  return {
    data,
    loading,
    error,
    clearError,
    reset,
    streamActivityLog,
  };
};
